/**
 * Utilidades para resumen de usuario y formato compacto.
 * Reduce tokens en el prompt de IA: resumen de gustos, límite de ítems y líneas compactas.
 */

/** Máximo de títulos "ya vistos" a incluir en el prompt (evita prompts enormes). */
export const MAX_WATCHED_IN_PROMPT = 50;

/** Máximo de ítems "mejor valorados" a incluir en el prompt. */
export const MAX_TOP_RATED_IN_PROMPT = 10;

/** Máximo de ítems "que no le gustaron" (baja calificación) a incluir en el prompt. */
export const MAX_LOW_RATED_IN_PROMPT = 10;

/** Máximo de recomendaciones existentes a incluir en el prompt (para evitar duplicados). */
export const MAX_EXISTING_RECOMMENDATIONS_IN_PROMPT = 50;

/** Umbral de calificación: por debajo o igual se considera "no le gustó". */
export const LOW_RATING_THRESHOLD = 5;

/** Umbral para "le gustaron": calificación >= 6.5. */
export const LIKED_RATING_THRESHOLD = 6.5;

/** Cantidad de ítems "le gustaron" (muestra aleatoria) en el prompt. */
export const MAX_LIKED_IN_PROMPT = 15;

/** Cantidad de ítems "no le gustaron" (muestra aleatoria) en el prompt. */
export const MAX_DISLIKED_IN_PROMPT = 8;

/**
 * Mezcla un array (Fisher-Yates). No muta el original.
 * @param {Array} arr - Array a mezclar
 * @returns {Array} Copia mezclada
 */
function shuffleArray(arr) {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Formatea un ítem de historial en una línea compacta: "Título (año) [género]"
 * Versión optimizada: solo muestra género si es relevante (máximo 1 género para ahorrar tokens)
 * @param {Object} item - Ítem de movie/series/music
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @param {boolean} ultraCompact - Si es true, omite género (solo título y año)
 * @returns {string}
 */
export function formatItemCompact(item, contentType = 'movie', ultraCompact = false) {
  const title = item.title || 'Sin título';
  const year = item.year || item.year_released || '?';
  
  if (ultraCompact) {
    // Formato ultra-compacto: solo título y año
    if (contentType === 'music' && item.artist) {
      return `${title} (${year})`;
    }
    return `${title} (${year})`;
  }
  
  // Formato normal: título, año y género (máximo 1 género para optimizar)
  let genre = '';
  if (Array.isArray(item.genre)) {
    genre = item.genre[0] || ''; // Solo el primer género
  } else if (item.genre) {
    genre = String(item.genre).split(',')[0]; // Solo el primer género si hay múltiples
  }
  
  if (contentType === 'music' && item.artist) {
    return `${title} – ${item.artist} (${year})${genre ? ` [${genre}]` : ''}`;
  }
  return `${title} (${year})${genre ? ` [${genre}]` : ''}`;
}

/**
 * Formatea un ítem con su calificación de usuario: "Título (año) [género] - N/10"
 * @param {Object} item - Ítem de movie/series/music (debe tener ratings.user_rating o user_rating)
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @returns {string}
 */
export function formatItemCompactWithRating(item, contentType = 'movie') {
  const line = formatItemCompact(item, contentType);
  const rating = item.ratings?.user_rating ?? item.user_rating ?? item.rating;
  const ratingStr = rating != null ? ` - ${Number(rating)}/10` : '';
  return line + ratingStr;
}

/**
 * Obtiene ítems de baja calificación (que no le gustaron) en formato compacto.
 * @param {Object} watchedContent - { movies, series, music }
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @param {number} limit - Máximo de ítems (default MAX_LOW_RATED_IN_PROMPT)
 * @param {number} maxRating - Calificación máxima para considerar "no le gustó" (default LOW_RATING_THRESHOLD)
 * @returns {string[]} Líneas compactas con calificación
 */
export function getLowRatedCompactLines(watchedContent, contentType, limit = MAX_LOW_RATED_IN_PROMPT, maxRating = LOW_RATING_THRESHOLD) {
  const key = contentType === 'movie' ? 'movies' : contentType === 'series' ? 'series' : 'music';
  const list = watchedContent?.[key] || [];
  const withUserRating = list
    .map((item) => ({
      ...item,
      _userRating: item.ratings?.user_rating ?? item.user_rating ?? item.rating
    }))
    .filter((item) => item._userRating != null && Number(item._userRating) <= maxRating);
  const sorted = [...withUserRating].sort((a, b) => (a._userRating || 0) - (b._userRating || 0));
  return sorted.slice(0, limit).map((item) => formatItemCompactWithRating(item, contentType));
}

/**
 * Obtiene una muestra ALEATORIA de ítems que le gustaron (rating >= minRating), con puntuación.
 * Cada petición verá un subconjunto distinto → más diversidad en recomendaciones.
 * @param {Object} watchedContent - { movies, series, music }
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @param {number} limit - Máximo de ítems (default MAX_LIKED_IN_PROMPT)
 * @param {number} minRating - Calificación mínima para "le gustó" (default LIKED_RATING_THRESHOLD = 6.5)
 * @returns {string[]} Líneas compactas con calificación
 */
export function getRandomLikedCompactLines(watchedContent, contentType, limit = MAX_LIKED_IN_PROMPT, minRating = LIKED_RATING_THRESHOLD) {
  const key = contentType === 'movie' ? 'movies' : contentType === 'series' ? 'series' : 'music';
  const list = watchedContent?.[key] || [];
  const withUserRating = list
    .map((item) => ({
      ...item,
      _userRating: item.ratings?.user_rating ?? item.user_rating ?? item.rating
    }))
    .filter((item) => item._userRating != null && Number(item._userRating) >= minRating);
  const shuffled = shuffleArray(withUserRating);
  return shuffled.slice(0, limit).map((item) => formatItemCompactWithRating(item, contentType));
}

/**
 * Obtiene una muestra ALEATORIA de ítems que no le gustaron (rating < maxRating), con puntuación.
 * @param {Object} watchedContent - { movies, series, music }
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @param {number} limit - Máximo de ítems (default MAX_DISLIKED_IN_PROMPT)
 * @param {number} maxRating - Calificación máxima para "no le gustó" (default 6.5)
 * @returns {string[]} Líneas compactas con calificación
 */
export function getRandomDislikedCompactLines(watchedContent, contentType, limit = MAX_DISLIKED_IN_PROMPT, maxRating = LIKED_RATING_THRESHOLD) {
  const key = contentType === 'movie' ? 'movies' : contentType === 'series' ? 'series' : 'music';
  const list = watchedContent?.[key] || [];
  const withUserRating = list
    .map((item) => ({
      ...item,
      _userRating: item.ratings?.user_rating ?? item.user_rating ?? item.rating
    }))
    .filter((item) => item._userRating != null && Number(item._userRating) < maxRating);
  const shuffled = shuffleArray(withUserRating);
  return shuffled.slice(0, limit).map((item) => formatItemCompactWithRating(item, contentType));
}

/**
 * Obtiene la lista de ítems "ya vistos" en formato compacto, limitada a N.
 * Prioriza los más recientes o mejor valorados.
 * @param {Object} watchedContent - { movies, series, music }
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @param {number} limit - Máximo de ítems (default MAX_WATCHED_IN_PROMPT)
 * @returns {string[]} Líneas compactas
 */
export function getWatchedCompactLines(watchedContent, contentType, limit = MAX_WATCHED_IN_PROMPT) {
  const key = contentType === 'movie' ? 'movies' : contentType === 'series' ? 'series' : 'music';
  const list = watchedContent?.[key] || [];
  const withDate = list.map((item) => ({
    ...item,
    _sortDate: item.watched_date || item.date_watched || ''
  }));
  const sorted = [...withDate].sort((a, b) => (b._sortDate || '').localeCompare(a._sortDate || ''));
  return sorted.slice(0, limit).map((item) => formatItemCompact(item, contentType));
}

/**
 * Obtiene una muestra ALEATORIA de ítems "ya vistos" en formato compacto.
 * Cada petición verá un subconjunto distinto → más diversidad.
 * @param {Object} watchedContent - { movies, series, music }
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @param {number} limit - Máximo de ítems (default MAX_WATCHED_IN_PROMPT)
 * @returns {string[]} Líneas compactas
 */
export function getRandomWatchedCompactLines(watchedContent, contentType, limit = MAX_WATCHED_IN_PROMPT) {
  const key = contentType === 'movie' ? 'movies' : contentType === 'series' ? 'series' : 'music';
  const list = watchedContent?.[key] || [];
  const shuffled = shuffleArray(list);
  return shuffled.slice(0, limit).map((item) => formatItemCompact(item, contentType));
}

/**
 * Obtiene los géneros más frecuentes del historial para un tipo de contenido.
 * @param {Object} watchedContent - { movies, series, music }
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @param {number} topN - Cuántos géneros devolver (default 8)
 * @returns {string[]}
 */
export function getTopGenres(watchedContent, contentType, topN = 8) {
  const key = contentType === 'movie' ? 'movies' : contentType === 'series' ? 'series' : 'music';
  const list = watchedContent?.[key] || [];
  const count = {};
  list.forEach((item) => {
    const genres = Array.isArray(item.genre) ? item.genre : (item.genre ? [item.genre] : []);
    genres.forEach((g) => {
      const g2 = (g || '').trim();
      if (g2) count[g2] = (count[g2] || 0) + 1;
    });
  });
  return Object.entries(count)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([g]) => g);
}

/**
 * Obtiene las décadas más vistas (ej: "1990s", "2000s").
 * @param {Object} watchedContent - { movies, series, music }
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @param {number} topN - Cuántas décadas (default 4)
 * @returns {string[]}
 */
export function getTopDecades(watchedContent, contentType, topN = 4) {
  const key = contentType === 'movie' ? 'movies' : contentType === 'series' ? 'series' : 'music';
  const list = watchedContent?.[key] || [];
  const count = {};
  list.forEach((item) => {
    const y = item.year || item.year_released;
    if (!y) return;
    const decade = `${Math.floor(Number(y) / 10) * 10}s`;
    count[decade] = (count[decade] || 0) + 1;
  });
  return Object.entries(count)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([d]) => d);
}

/**
 * Construye un texto de resumen de gustos para inyectar en el prompt.
 * @param {Object} userData - { preferences, watchedContent }
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @returns {string}
 */
export function buildUserSummaryText(userData, contentType = 'movie') {
  const prefs = userData.preferences || {};
  const watched = userData.watchedContent || {};
  const parts = [];

  const topGenres = getTopGenres(watched, contentType, 6);
  if (topGenres.length) {
    parts.push(`Géneros más vistos: ${topGenres.join(', ')}`);
  }

  const topDecades = getTopDecades(watched, contentType, 3);
  if (topDecades.length) {
    parts.push(`Décadas frecuentes: ${topDecades.join(', ')}`);
  }

  if (contentType !== 'music' && (prefs.favorite_actors?.length || prefs.favorite_genres?.length)) {
    const actors = (prefs.favorite_actors || []).slice(0, 5).join(', ');
    const genres = (prefs.favorite_genres || []).slice(0, 5).join(', ');
    if (actors) parts.push(`Actores favoritos (preferencia): ${actors}`);
    if (genres) parts.push(`Géneros favoritos (preferencia): ${genres}`);
  }

  if (contentType === 'music' && prefs.favorite_genres?.length) {
    parts.push(`Géneros favoritos: ${(prefs.favorite_genres || []).slice(0, 5).join(', ')}`);
  }

  if (prefs.max_recommendations) {
    parts.push(`Quiere hasta ${prefs.max_recommendations} recomendaciones`);
  }

  return parts.length ? parts.join('. ') : 'Sin resumen de gustos aún.';
}

/** Máximo de géneros/títulos a usar en el resumen cruzado para "primeras recomendaciones". */
const CROSS_CONTENT_TOP_GENRES = 4;
const CROSS_CONTENT_TOP_TITLES = 3;

/**
 * Resumen de gustos de OTROS tipos de contenido (para generar primeras recomendaciones
 * cuando el usuario tiene poco o ningún historial en el tipo objetivo).
 * Ej: si pide series, devuelve gustos de películas y música en pequeña escala.
 * @param {Object} watchedContent - { movies, series, music }
 * @param {string} targetContentType - 'movie' | 'series' | 'music' (el que estamos recomendando)
 * @returns {string}
 */
export function buildCrossContentSummary(watchedContent, targetContentType) {
  const parts = [];
  if (targetContentType === 'movie') {
    const seriesGenres = getTopGenres(watchedContent, 'series', CROSS_CONTENT_TOP_GENRES);
    const musicGenres = getTopGenres(watchedContent, 'music', CROSS_CONTENT_TOP_GENRES);
    const seriesTitles = (watchedContent?.series || [])
      .slice(0, CROSS_CONTENT_TOP_TITLES)
      .map((s) => s.title)
      .filter(Boolean);
    const musicTitles = (watchedContent?.music || [])
      .slice(0, CROSS_CONTENT_TOP_TITLES)
      .map((m) => (m.artist ? `${m.title} (${m.artist})` : m.title))
      .filter(Boolean);
    if (seriesGenres.length) parts.push(`Series que le gustan (géneros): ${seriesGenres.join(', ')}`);
    if (seriesTitles.length) parts.push(`Algunas series vistas: ${seriesTitles.join(', ')}`);
    if (musicGenres.length) parts.push(`Música que escucha (géneros): ${musicGenres.join(', ')}`);
    if (musicTitles.length) parts.push(`Algunos títulos de música: ${musicTitles.join(', ')}`);
  } else if (targetContentType === 'series') {
    const movieGenres = getTopGenres(watchedContent, 'movie', CROSS_CONTENT_TOP_GENRES);
    const musicGenres = getTopGenres(watchedContent, 'music', CROSS_CONTENT_TOP_GENRES);
    const movieTitles = (watchedContent?.movies || [])
      .slice(0, CROSS_CONTENT_TOP_TITLES)
      .map((m) => m.title)
      .filter(Boolean);
    const musicTitles = (watchedContent?.music || [])
      .slice(0, CROSS_CONTENT_TOP_TITLES)
      .map((m) => (m.artist ? `${m.title} (${m.artist})` : m.title))
      .filter(Boolean);
    if (movieGenres.length) parts.push(`Películas que le gustan (géneros): ${movieGenres.join(', ')}`);
    if (movieTitles.length) parts.push(`Algunas películas vistas: ${movieTitles.join(', ')}`);
    if (musicGenres.length) parts.push(`Música que escucha (géneros): ${musicGenres.join(', ')}`);
    if (musicTitles.length) parts.push(`Algunos títulos de música: ${musicTitles.join(', ')}`);
  } else if (targetContentType === 'music') {
    const movieGenres = getTopGenres(watchedContent, 'movie', CROSS_CONTENT_TOP_GENRES);
    const seriesGenres = getTopGenres(watchedContent, 'series', CROSS_CONTENT_TOP_GENRES);
    const movieTitles = (watchedContent?.movies || [])
      .slice(0, CROSS_CONTENT_TOP_TITLES)
      .map((m) => m.title)
      .filter(Boolean);
    const seriesTitles = (watchedContent?.series || [])
      .slice(0, CROSS_CONTENT_TOP_TITLES)
      .map((s) => s.title)
      .filter(Boolean);
    if (movieGenres.length) parts.push(`Películas que le gustan (géneros): ${movieGenres.join(', ')}`);
    if (movieTitles.length) parts.push(`Algunas películas vistas: ${movieTitles.join(', ')}`);
    if (seriesGenres.length) parts.push(`Series que le gustan (géneros): ${seriesGenres.join(', ')}`);
    if (seriesTitles.length) parts.push(`Algunas series vistas: ${seriesTitles.join(', ')}`);
  }
  return parts.length ? parts.join('. ') : 'Sin historial en otros contenidos aún.';
}

/**
 * Formatea recomendaciones existentes en líneas compactas para el prompt.
 * Formato con género para mejor identificación: "Título (año) [género]"
 * Prioriza las más recientes (últimas añadidas).
 * @param {Array} existingRecommendations - Array de recomendaciones existentes
 * @param {string} contentType - 'movie' | 'series' | 'music'
 * @param {number} limit - Máximo de ítems (default MAX_EXISTING_RECOMMENDATIONS_IN_PROMPT)
 * @returns {string[]} Líneas compactas
 */
export function getExistingRecommendationsCompactLines(existingRecommendations = [], contentType = 'movie', limit = MAX_EXISTING_RECOMMENDATIONS_IN_PROMPT) {
  if (!Array.isArray(existingRecommendations) || existingRecommendations.length === 0) {
    return [];
  }
  
  // Tomar las últimas N recomendaciones (las más recientes)
  const recentRecommendations = existingRecommendations.slice(-limit);
  
  // Formato con género para mejor identificación (como funcionaba antes con ~20% duplicados)
  return recentRecommendations.map((item) => {
    const title = item.title || 'Sin título';
    const year = item.year || item.year_released || '?';
    let genre = '';
    if (Array.isArray(item.genre)) {
      genre = item.genre.slice(0, 1).join(', '); // Solo primer género para optimizar
    } else if (item.genre) {
      genre = String(item.genre).split(',')[0]; // Solo primer género
    }
    if (contentType === 'music' && item.artist) {
      return `${title} – ${item.artist} (${year})${genre ? ` [${genre}]` : ''}`;
    }
    return `${title} (${year})${genre ? ` [${genre}]` : ''}`;
  });
}
